/* mg-mm-image-warm.js - 7 Aug 2026 (Will). Required companion to the LCP fix in
   snippets/header__dropdown-menu.liquid. Deleting one without the other leaves the mega menu
   with no preview images at all.

   ============================== WHY THIS EXISTS ==============================
   The mega-menu preview images were served as ORIGINAL uploads (the snippet used `img_url:''` - an
   empty size string) AND fetched on every page view, because they sit in a panel that is
   visibility:hidden rather than display:none. Measured on the live homepage:

       17 images | 2.2 MB | 58% of ALL image bytes | every one starting BEFORE the hero finished

   That is the shape of the LCP P75 regression (1.8s -> 11s).

   ============================== WHY loading="lazy" WAS NOT ENOUGH ==============================
   Two separate failures, both measured, both worth recording so nobody retries them:

   1. Chrome will not re-evaluate these as lazy candidates when the panel opens. After adding
      loading="lazy", opening Shop rendered all 14 images at 386x464 with naturalWidth=0 - still
      unloaded 3s later - despite rect top:108, intersectsViewport:true, visibility:visible on every
      ancestor. They were classified at parse time (0x0, hidden panel) and never revisited. A menu of
      blank boxes.

   2. Something in the theme's own JS re-inserts the mega-menu markup and flips the images to
      loading="eager" (some end up with the attribute stripped to null entirely). Measured: images
      began downloading at 1988ms - BEFORE the load event at 2401ms and long before this script's
      warm-up at 5401ms. So the lazy attribute cannot be relied on here at all.

   ============================== THE APPROACH THAT WORKS ==============================
   Do not give the browser a src to fetch. The snippet now renders `data-mg-src` and no `src`, so
   there is nothing to load early no matter what the theme JS does to the attributes. This script
   assigns the real src at a moment of our choosing:
     - immediately on first mouseover of the nav row (user intent), or
     - WARM_DELAY_MS after the window load event, via requestIdleCallback (background),
   whichever comes first.

   TRADE-OFF, stated plainly: with JS disabled the mega-menu preview images do not appear. The menu
   links, which are the functional part, are unaffected. Accepted deliberately - the alternative was
   2.2MB in the critical path of every page view.

   NOTES: the host snippet renders once per dropdown so this tag appears several times - hence the
   window guard. jQuery also strips <script> tags from the DOM after executing them when the theme
   moves the mega-menu markup, so you will not see the tag in DevTools even though it ran.

   To revert: restore `src` in the snippet and delete this file plus its script tag. */

/* ============================== MG 24 Sep 2026 – NO BACKGROUND WARM-UP ON TOUCH ==============================
   Measured on the live product page (Lighthouse mobile, V16 and V17): the 2.5 s idle warm-up below
   fetched the 19 desktop mega-menu previews on phones too – 1.3–1.9 MB per page view for a menu a
   phone never opens (the mobile menu has no images; checked, 0 <img> in .mobile-menu). It ran after
   `load`, so it never touched real-user LCP, but it was pure waste on mobile data.

   Now: the idle warm-up is scheduled only on a hover-capable, fine-pointer device at a desktop
   width (the same >= 799 px breakpoint the header uses). The hover trigger and the controller's
   call on first open (window.__mgWarmMegaMenuImages, mg-menu.js) are unchanged, so desktop
   behaviour is identical and a keyboard-first open still gets images.
   ROLLBACK: delete the `DESKTOP` test in schedule() – i.e. restore `schedule();` unconditionally.
   Record: changelog-2026-09-24-mm-warm-touch-gate.md
   ============================================================================================================ */
(function () {
  if (window.__mgMMWarmBound) return;
  window.__mgMMWarmBound = 1;

  var WARM_DELAY_MS = 2500; /* after window load, so LCP is long settled */
  var warmed = false;
  var DESKTOP = !!(window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 799px)').matches);

  function warm() {
    if (warmed) return;
    warmed = true;
    var imgs = document.querySelectorAll('.mega-menu-images img[data-mg-src]');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('data-mg-src');
      if (src) {
        imgs[i].src = src;
        imgs[i].removeAttribute('data-mg-src');
      }
    }
  }
  window.__mgWarmMegaMenuImages = warm; /* exposed for testing */

  function bind() {
    var row = document.querySelector('.header__menu-items');
    if (row) row.addEventListener('mouseover', warm, { once: true, capture: true });

    var schedule = function () {
      if (!DESKTOP) return; /* MG 24 Sep 2026: phones never open the desktop menu */
      setTimeout(function () {
        if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
        else warm();
      }, WARM_DELAY_MS);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
